// Immediate hover tooltip for FlowSight charts.
//
// dc's own `.renderTitle()` emits an SVG <title>, which the browser shows only
// after a ~1s dwell and paints in OS chrome that ignores the dark theme. That
// is fine for an afterthought, but not when the tooltip IS the way to read a
// value - the bubble chart's X axis, for instance, repeats the same tick label
// whenever the data collapses into one maturity band.
//
// One singleton node for the whole app: charts are torn down and rebuilt on
// every pipeline refresh, and a per-chart element would pile up copies in the
// body on each rebuild.
let node = null

const ensureNode = () => {
  if (node && document.body.contains(node)) return node
  node = document.createElement('div')
  node.className = 'flowsight-chart-tooltip'
  node.style.cssText = [
    'position:fixed',
    'z-index:9999',
    'pointer-events:none',
    'opacity:0',
    'transition:opacity .08s ease',
    'background:var(--surface-2, #1b1e24)',
    'border:1px solid var(--border, #2a2e36)',
    'border-radius:6px',
    'padding:8px 10px',
    'font-size:12px',
    'line-height:1.45',
    'color:var(--text, #e6e7ea)',
    'box-shadow:0 4px 16px rgba(0,0,0,.45)',
    'max-width:260px',
    'white-space:nowrap',
  ].join(';')
  document.body.appendChild(node)
  return node
}

const OFFSET = 14

// A tooltip is decoration and must never be able to take the page down. dc
// dispatches through its own nested d3-selection, so a caller that reads the
// wrong listener argument can land here with null instead of an event - fall
// back to the origin rather than throwing on .clientX.
const pointerOf = (event) => ({
  x: Number.isFinite(event?.clientX) ? event.clientX : 0,
  y: Number.isFinite(event?.clientY) ? event.clientY : 0,
})

const place = (el, event) => {
  // Flips to the other side of the cursor near the viewport edge, so the
  // tooltip never gets clipped by the window (it is position:fixed, so the
  // chart's own overflow doesn't clip it).
  const { innerWidth, innerHeight } = window
  const { x: clientX, y: clientY } = pointerOf(event)
  const rect = el.getBoundingClientRect()
  const left = clientX + OFFSET + rect.width > innerWidth
    ? clientX - OFFSET - rect.width
    : clientX + OFFSET
  const top = clientY + OFFSET + rect.height > innerHeight
    ? clientY - OFFSET - rect.height
    : clientY + OFFSET
  el.style.left = `${Math.max(4, left)}px`
  el.style.top = `${Math.max(4, top)}px`
}

export function getChartTooltip () {
  return {
    // `html` is built by the caller from its own data - keep it to text and
    // simple markup, it is injected as innerHTML.
    show (html, event) {
      const el = ensureNode()
      el.innerHTML = html
      el.style.opacity = '1'
      place(el, event)
    },
    move (event) {
      if (!node || node.style.opacity === '0') return
      place(node, event)
    },
    hide () {
      if (node) node.style.opacity = '0'
    },
  }
}

// Escapes values that come from the dataset (counterparty names and the like)
// before they go into innerHTML.
export const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
